import { useState, type CSSProperties } from 'react';

export const CONTACT_EMAIL = 'contact@oriscen.ai';

/**
 * Contact address as a mailto link that also copies to the clipboard on click.
 *
 * Why the copy fallback: a bare `mailto:` does nothing on machines with no
 * registered default mail client — common on Windows where people live in
 * webmail — so clicking "Contact" silently no-ops there. We keep the mailto
 * (Mac/iOS/Android open a compose window as before) and, in parallel, copy the
 * address and flash "Copied ✓" so the click always produces a visible result.
 * The visible text is the address itself so it stays selectable even if both
 * paths fail.
 */
export default function ContactLink({
  className,
  style,
  label,
}: {
  className?: string;
  style?: CSSProperties;
  /** Optional short link text (e.g. "Contact" in a nav). Defaults to the address. */
  label?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleClick = () => {
    try {
      navigator.clipboard?.writeText(CONTACT_EMAIL).then(
        () => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        },
        () => {},
      );
    } catch {
      /* clipboard unavailable — mailto still fires where a handler exists */
    }
  };

  return (
    <a
      href={`mailto:${CONTACT_EMAIL}`}
      className={className}
      style={style}
      onClick={handleClick}
      aria-label={`Email us at ${CONTACT_EMAIL}`}
    >
      {copied ? 'Copied ✓' : label ?? CONTACT_EMAIL}
    </a>
  );
}
